// Admin lot-state store backing the RealtimeLotsProvider
// (`./RealtimeLotsProvider.tsx`). ADR 042 §§1–4 (BL-1251).
//
// BL-1262: the engine — the LWW-per-entity fold, cursor, cold-seed + reconcile,
// connection tracking — moved to the generic `./entity-state-store.ts` when the
// registrations stream needed the identical machinery. This file is now the
// lot-shaped BINDING of that engine: it supplies the four per-stream details
// (`lotId` as the fold key, the `sequence` ordering key, the `lot_state.delta` event,
// and the catch-up response's `lots` collection) and re-exposes the same public API this
// module has always had. `LotStateStore`, `applyDeltaToMap`, `foldLotStateDeltas`,
// `LotStateConnection`, and `DISCONNECTED_LOT_STATE` are unchanged in name,
// signature, and behaviour — BL-1251's store + connection tests pass against this
// file unmodified, which is what proves the extraction was behaviour-preserving.
//
// The admin lot-state stream is a *keyed snapshot* (ADR 011 §2c): the server broadcasts
// one changed lot's absolute scalars per mutation (`lot_state.delta`), folded into a
// `Map<lotId, AdminLotStateDelta>` keyed on a per-AUCTION sequence. See
// `entity-state-store.ts` for the full idempotent / order-tolerant rationale, and for
// the gap detection that sequence turns on (BL-1628 — the fold alone was never
// gap-healing, per BL-1627).
//
// This binding is still exactly the four per-stream details, unchanged in count by
// BL-1628: gap detection lives in the engine and is switched on by the PAYLOAD
// carrying a `sequence`, not by anything declared here. That is what made BL-1629
// (max bids) a server-side change plus one renamed field — CONFIRMED, not predicted:
// its client diff was `cursorOf` and two type members. BL-1630 (registrations) was the
// last one left and confirmed it a third time, so the claim now rests on three
// instances rather than on a prediction.

import {
    decodeAdminLotStateDelta,
    type AdminLotStateDelta,
    type AdminLotStateDeltaDecoded,
    type LotStateCatchupResult,
} from '../../mappers/realtime/admin-lot-state';
import { formatRealtimeFailure } from '../../isomorphic/realtime/realtime-failure-log';
import type { LotStateDepartedEvent } from '../../types/realtime/events';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';
import type { Unsubscribe } from '../../types/realtime/types';
import {
    applyDeltaToEntityMap,
    foldEntityDeltas,
    EntityStateStore,
    DISCONNECTED_ENTITY_STATE,
    type EntityStateConnection,
    type NotifyScheduler,
} from './entity-state-store';

// The catch-up result is declared once, beside the delta it carries, so the route
// registry can type the response without importing this client store (BL-2009,
// ADR 027 — Response Contract).
export type { LotStateCatchupResult };

/**
 * What a lot-state store covers — an auction's whole catalog, or ONE lot
 * (BL-1542 AC 35).
 *
 * A UNION, never `{ auctionId: string; lotId?: string }`. A list, or an optional
 * second field, would let a caller name lots across two auctions — and this
 * stream's topic is derived from a single auction, so there would be no topic to
 * subscribe to. The type refuses to express the request rather than the route
 * rejecting it later.
 */
export type LotStateScope = { auctionId: string; lotId?: undefined } | { lotId: string; auctionId?: undefined };

/** The id a scope folds under — the auction, or the single lot. */
export function lotStateScopeId(scope: LotStateScope): string {
    return scope.lotId ?? scope.auctionId;
}

/**
 * Fetcher seam: given the scope + the client's current position, hit the
 * authenticated admin lot-state catch-up route. `sinceSequence === 0` is the
 * cold-seed. The app injects this via RealtimeLotsProvider.
 *
 * The scope reaches the fetcher as an object rather than a bare id because the
 * route takes `?auction_id=` or `?lot_id=` and the two are not interchangeable —
 * a bare string would leave the fetcher guessing which it had been handed.
 */
export type LotStateCatchupFetcher = (
    scope: LotStateScope,
    sinceSequence: number,
) => Promise<LotStateCatchupResult>;

/**
 * The lot stream's binding of the generic engine's per-stream details.
 *
 * A FACTORY since BL-1542, so a lot-scoped store can close a `retains`
 * predicate over its lot. The unscoped shape (`lotStateConfig()`) is
 * byte-identical in behaviour to the const this replaced — `retains` is simply
 * absent, and the engine skips the check.
 */
function lotStateConfig(lotId?: string) {
    const decode = createLotStateDecoder();
    return {
        ...LOT_STATE_KEYS,
        itemsOf: (result: LotStateCatchupResult) => decode(result.lots),
        // BUG-1517 — this stream's topic is per-AUCTION, so one frame carries every
        // lot a transaction changed (length 1 for a single mutation).
        deltasOf: (payload: unknown) =>
            decode(
                (payload as { states: ReadonlyArray<{ state: AdminLotStateDelta }> }).states.map(
                    (entry) => entry.state,
                ),
            ),
        // The topic is per-AUCTION, so a lot-scoped store still RECEIVES every
        // lot's deltas. It counts them (cursor + gap detection) and keeps only
        // its own — see `EntityStateStoreConfig.retains` for why the filter
        // cannot sit any earlier than the fold.
        ...(lotId == null ? {} : { retains: (d: AdminLotStateDeltaDecoded) => d.lotId === lotId }),
        // BUG-1766 — a lot moved or removed out of the auction is announced on this
        // topic. Every store counts the numbers; only the auction-scoped one drops the
        // lot and checks membership. A lot-scoped reader (the bid record) re-seeds on
        // its own lot's new auction when it next mounts.
        departures: {
            event: 'lot_state.departed' as const,
            entriesOf: (payload: unknown) =>
                (payload as LotStateDepartedEvent['payload']).departed.map((d) => ({ key: d.lotId, cursor: d.sequence })),
            removes: lotId == null,
        },
        ...(lotId == null
            ? {
                  membership: {
                      countOf: (result: LotStateCatchupResult) => result.liveLotCount,
                      // The fold keeps deleted tombstones (BL-1764); they are not live.
                      isLive: (lot: AdminLotStateDeltaDecoded) => !isLotStateTombstone(lot),
                  },
              }
            : {}),
    };
}

/**
 * The ONE decode point between the wire and every lot-state reader (BL-2009,
 * ADR 060 — Money Type): each arriving delta — live frame or catch-up — has its
 * six MoneyWire fields decoded into `Money` here, once, so `useLots` / `useLot`
 * / `useLotStateForRecord` and everything below them hold `Money` and never
 * re-parse an amount or pair it with a currency from elsewhere.
 *
 * A delta carrying a malformed amount is logged (once per lot + sequence) and
 * left out rather than crashing the console. The server only emits MoneyWire
 * (the mapper throws on an unsupported currency before publishing), so this is
 * a defence against a bug, not a path the stream takes. Skipping a live delta
 * leaves its sequence unseen, so the next frame reads as a gap and the engine's
 * catch-up advances the position from the RESULT — the stream cannot stall.
 *
 * @returns A decoder bound to one store, with its own log-once memory.
 */
function createLotStateDecoder(): (
    wire: readonly AdminLotStateDelta[],
) => AdminLotStateDeltaDecoded[] {
    const logged = new Set<string>();
    return (wire) => {
        const out: AdminLotStateDeltaDecoded[] = [];
        for (const delta of wire) {
            const result = decodeAdminLotStateDelta(delta);
            if (result.kind === 'ok') {
                out.push(result.delta);
                continue;
            }
            const key = `${delta.lotId}:${delta.sequence}`;
            if (logged.has(key)) continue;
            logged.add(key);
            // eslint-disable-next-line no-console
            console.error(
                ...formatRealtimeFailure(
                    '[LotStateStore] undecodable lot-state money field — delta skipped',
                    { lotId: delta.lotId, sequence: delta.sequence, field: result.field },
                    new Error(`unrecognised ${result.field} shape: ${JSON.stringify(delta[result.field])}`),
                ),
            );
        }
        return out;
    };
}

/** The per-stream details every lot store shares: fold key, ordering key, event. */
const LOT_STATE_KEYS = {
    keyOf: (delta: AdminLotStateDeltaDecoded) => delta.lotId,
    cursorOf: (delta: AdminLotStateDeltaDecoded) => delta.sequence,
    event: 'lot_state.delta' as const,
};

/**
 * Apply one delta to the fold map with last-writer-wins per lot. Mutates `map`.
 * Returns `true` iff the map changed (the delta was newer than what we held).
 *
 * The guard is `> existing.sequence` (strict): an equal-or-lower sequence is a
 * duplicate / stale / out-of-order delivery and is dropped. This is what makes
 * the fold idempotent and order-independent (AC 3).
 */
export function applyDeltaToMap(
    map: Map<string, AdminLotStateDeltaDecoded>,
    delta: AdminLotStateDeltaDecoded,
): boolean {
    return applyDeltaToEntityMap(
        map,
        delta,
        LOT_STATE_KEYS.keyOf,
        LOT_STATE_KEYS.cursorOf,
    );
}

/**
 * Fold a batch of deltas into a fresh map with last-writer-wins per lot. Pure —
 * the result is identical regardless of the input order or duplicates (AC 3).
 */
export function foldLotStateDeltas(
    deltas: readonly AdminLotStateDeltaDecoded[],
): Map<string, AdminLotStateDeltaDecoded> {
    return foldEntityDeltas(deltas, LOT_STATE_KEYS.keyOf, LOT_STATE_KEYS.cursorOf);
}

/**
 * Whether a lot-state delta is a soft-delete TOMBSTONE (BL-1764, ADR 018 — Soft Delete).
 *
 * The admin lot-state stream broadcasts a deleted lot with `deletedAt` stamped
 * (BUG-1513) precisely so subscribers learn it is gone; a consumer that keeps it
 * treats a removed lot as live. `deletedAt` is the decision stamp (ADR 023 —
 * Predicate Lifecycle), so the predicate reads it and nothing else.
 *
 * @param delta - any shape carrying the lot's `deletedAt` stamp.
 * @returns `true` iff the lot has been soft-deleted.
 */
export function isLotStateTombstone(delta: Pick<AdminLotStateDeltaDecoded, 'deletedAt'>): boolean {
    return delta.deletedAt != null;
}

/**
 * The READER's view of a lot fold: every lot that has not been soft-deleted
 * (BL-1764). Pure.
 *
 * Returns the SAME reference when the fold holds no tombstone — the common case
 * on every sale — so a `useSyncExternalStore` snapshot built on it allocates
 * nothing and stays ref-stable.
 *
 * @param lots - the folded map, tombstones included.
 * @returns the map without tombstones (the input itself when there are none).
 */
export function withoutTombstones(
    lots: ReadonlyMap<string, AdminLotStateDeltaDecoded>,
): ReadonlyMap<string, AdminLotStateDeltaDecoded> {
    for (const delta of lots.values()) {
        if (isLotStateTombstone(delta)) {
            return new Map([...lots].filter(([, lot]) => !isLotStateTombstone(lot)));
        }
    }
    return lots;
}

/**
 * Whether the store has established its stream. Consumers render a connection
 * banner from this (BL-1254 AC 8) — a lot COUNT can't stand in for it, since an
 * auction may legitimately have zero lots.
 *
 * Alias of the generic `EntityStateConnection`; kept as a named export because
 * BL-1251's consumers (`useLotsConnection`, ConnectionHealthBanner) import it.
 */
export type LotStateConnection = EntityStateConnection;

/** Stable pre-connection value (ref-stable for `useSyncExternalStore`). */
export const DISCONNECTED_LOT_STATE: LotStateConnection = DISCONNECTED_ENTITY_STATE;

/**
 * The admin lot-state store for one auction — or, since BL-1542, for ONE LOT.
 * Owns the transport subscription,
 * the LWW-per-lot fold, the cursor, and cold-seed / reconcile catch-up. The
 * RealtimeLotsProvider constructs one per `auctionId` and drives `start()` /
 * `stop()` / `reconcile()`; the `useLots` / `useLot` selectors read via
 * `subscribe()` + `getLots()` (the `useSyncExternalStore` contract), and
 * `useLotsConnection()` reads `getConnection()`.
 *
 * A thin lot-shaped facade over `EntityStateStore` — `getLots()` is the domain
 * name for the engine's `getEntities()`; everything else is inherited unchanged.
 */
export class LotStateStore extends EntityStateStore<
    AdminLotStateDeltaDecoded,
    LotStateCatchupResult
> {
    constructor(
        subscriber: RealtimeSubscriber,
        fetcher: LotStateCatchupFetcher,
        scope: LotStateScope,
        /**
         * Override the coalesced-notify scheduler (BUG-1407). Tests inject a
         * manual pump; production takes the default short-timer window.
         */
        scheduleNotify?: NotifyScheduler,
    ) {
        // The engine's fetcher seam is `(scopeId: string, since) => …`, shared
        // with the other streams. The scope is bound HERE rather than widening
        // that seam, so a two-shaped scope stays a lot-stream concern and the
        // registration + max-bid bindings are untouched.
        super(
            subscriber,
            (_scopeId, since) => fetcher(scope, since),
            lotStateScopeId(scope),
            lotStateConfig(scope.lotId),
            scheduleNotify,
        );
    }

    /** The fold `liveLots` was derived from — the memo key for {@link getLots}. */
    private liveLotsSource: ReadonlyMap<string, AdminLotStateDeltaDecoded> | null = null;
    /** The tombstone-free view of `liveLotsSource`. */
    private liveLots: ReadonlyMap<string, AdminLotStateDeltaDecoded> = new Map();

    /**
     * The current folded lot map, WITHOUT soft-deleted lots (BL-1764). Stable
     * reference until a fold changes it.
     *
     * THE ONE PLACE A TOMBSTONE IS DROPPED, and why it is here rather than in the
     * fold or in a consumer:
     *
     * • Not the fold. The tombstone has to STAY in the engine's map: it is the
     *   highest-sequence entry for that lot, and LWW is what stops a stale,
     *   out-of-order frame for the same lot from resurrecting it. Deleting the key
     *   would reopen exactly that hole.
     * • Not a consumer. The rail, the catalog count, the "lots closed" tile, the
     *   workspace's `useLot` and the bid record all read this map; filtering in
     *   each would be five encodings of one rule, and any one left out disagrees
     *   with the rest.
     *
     * Here, every reader of the lot stream gets the same answer a page load gives
     * — the cold seed already omits deleted lots — so a clerk who stayed
     * connected, one who reconnected, and one who reloaded converge (ADR 011 —
     * Durable Events). Dropping the lot also unmounts the workspace's per-lot
     * ledger / max-bid subscriptions, which is what ends their 404 reconcile loop.
     *
     * Cost: one O(lots) scan per PUBLISHED fold (a coalesced flush, not a delta),
     * the same order as the `[...map.values()]` `useLots` already does per flush.
     */
    getLots(): ReadonlyMap<string, AdminLotStateDeltaDecoded> {
        const folded = this.getEntities();
        if (folded !== this.liveLotsSource) {
            this.liveLotsSource = folded;
            this.liveLots = withoutTombstones(folded);
        }
        return this.liveLots;
    }
}

export type { Unsubscribe };
